import { useCallback } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import retry from 'async-await-retry';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { desksEndpoints, integrationsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { deskCache } from '@/hooks/useDesks';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { VOICE_PROVIDERS_ENDPOINT } from '@/hooks/useVoiceProviders';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Desk } from '@/shared/types/desk';
import {
  Integration,
  IntegrationId,
  IntegrationType,
  NewIntegration,
  WhatsAppIntegration,
} from '@/shared/types/integration';

import { useRuleMutations } from '../modules/rules/hooks/useRules';

export const API = Object.freeze({
  listIntegrations: async <T extends Integration>(deskId: string): Promise<Integrations<T>> =>
    callGet(endpoints.integrations(deskId)),

  getIntegration: async <T extends Integration>(
    deskId: string,
    integrationId: string
  ): Promise<T> => callGet(endpoints.integration(deskId, integrationId)),

  createIntegration: async <T extends Integration>({
    desk_id,
    ...integration
  }: NewIntegration<T>): Promise<T> => callPost(endpoints.integrations(desk_id), integration),

  updateIntegration: async <T extends Integration>({
    desk_id,
    integration_id,
    ...integration
  }: IntegrationId<T>): Promise<T> => {
    return callPut(endpoints.integration(desk_id, integration_id), integration);
  },

  deleteIntegration: async ({
    desk_id,
    integration_id,
  }: IntegrationId<Integration>): Promise<IntegrationId<Integration>> =>
    callDelete(endpoints.integration(desk_id, integration_id)),

  verifyIntegration: async (desk_id: string, integration_id: string): Promise<Integration> =>
    callPost(endpoints.verify(desk_id, integration_id), {}),

  generateApiKey: async (desk_id: string, integration_id: string): Promise<{ api_key: string }> =>
    callPost(endpoints.generateApiKey(desk_id, integration_id), {}),
});

type MutationOptions = { hideErrors?: boolean; successDescription?: string };

type WithMutationOptions<T> = T & { mutationOptions?: MutationOptions };

export type Integrations<T = Integration> = { integrations: T[] };

/** Desks embed their integrations, so a cached desk is patched alongside the integration list. */
const patchDeskIntegrations = (
  queryClient: QueryClient,
  deskId: string,
  patch: (integrations: Desk['integrations']) => Desk['integrations']
) => {
  const desk = queryClient.getQueryData<Desk>([desksEndpoints.desk(deskId)]);
  if (desk) {
    deskCache.updated(queryClient, { ...desk, integrations: patch(desk.integrations || []) });
  }
};

export const integrationCache = createEntityCache<Integration, 'integrations'>({
  listKey: (deskId) => [endpoints.integrations(deskId)],
  detailKey: (deskId, integrationId) => [endpoints.integration(deskId, integrationId)],
  listField: 'integrations',
  getId: (integration) => integration.integration_id,
  getParentId: (integration) => integration.desk_id,
  afterChange: (queryClient, change) => {
    if (change.type === 'created') {
      patchDeskIntegrations(queryClient, change.parentId, (integrations) => [
        ...integrations.filter((item) => item.integration_id !== change.entity.integration_id),
        change.entity,
      ]);
    } else if (change.type === 'updated') {
      queryClient.invalidateQueries({ queryKey: [desksEndpoints.desk(change.parentId)] });
    } else {
      patchDeskIntegrations(queryClient, change.parentId, (integrations) =>
        integrations.filter((item) => item.integration_id !== change.id)
      );
    }
  },
});

export const useIntegrations = <T extends Integration = Integration>(
  deskId: string,
  integrationId?: string
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { markAsComplete } = useHomeCheckList();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const slug = useSelector(selectAccountSlug);
  const { deleteRule } = useRuleMutations(deskId);

  const { data: integrations, status: listStatus } = useQuery<Integrations<T>, Error>({
    queryKey: [endpoints.integrations(deskId)],
    queryFn: () => API.listIntegrations(deskId),
    enabled: Boolean(deskId),
  });

  const {
    data: integration,
    status: getStatus,
    error: getError,
  } = useQuery<T, Error>({
    queryKey: [endpoints.integration(deskId, integrationId)],
    queryFn: () => API.getIntegration(deskId, integrationId),
    enabled: Boolean(deskId && integrationId),
  });

  const verifyIntegrationAndUpdateQuery = useCallback(
    async (integration: Integration): Promise<Integration> => {
      if (integration?.type === 'web' || !integration?.active) {
        return;
      }
      try {
        await retry(
          async (i: Integration) => {
            const result = await API.getIntegration(i.desk_id, i.integration_id);
            integrationCache.updated(queryClient, result);
            queryClient.setQueryData<Integration>(
              [`${endpoints.integration(result.desk_id, result.integration_id)}/verify`],
              (prev) => ({ ...prev, ...result })
            );

            if (result.status === 'unverified' || result.status === 'verifying') {
              return Promise.reject(result);
            }
            return Promise.resolve(result);
          },
          [integration],
          {
            // Initial attempt plust the number of retries
            retriesMax: 4,
            // Retries after 2s, 4s and 8s
            interval: 2000,
            exponential: true,
            factor: 2,
          }
        );
      } catch (error) {
        console.error('Integration failed to verify ', error);
      }
    },
    [queryClient]
  );

  const { mutate: verifyIntegration, status: verifyStatus } = useMutation<Integration, Error>({
    mutationFn: () => API.verifyIntegration(deskId, integrationId),
    onSuccess: async (resp) => {
      // Refetch to get the new integration's verification status
      verifyIntegrationAndUpdateQuery(resp);
    },
    onError: (error) => {
      console.error('Error verifying integration', error);
    },
  });

  const { mutate: generateApiKey, status: generateApiKeyStatus } = useMutation<
    { api_key: string },
    Error,
    { deskId: string; integrationId: string }
  >({ mutationFn: ({ deskId, integrationId }) => API.generateApiKey(deskId, integrationId) });

  const {
    mutate: updateIntegration,
    status: updateStatus,
    error: updateError,
  } = useMutation<T, Error, WithMutationOptions<IntegrationId<T>>>({
    mutationFn: ({ mutationOptions: _, ...integration }) => {
      return API.updateIntegration<T>(integration as IntegrationId<T>);
    },
    onSuccess: async (resp, variables) => {
      if (resp?.active) {
        markAsComplete(AccountUserPrefsEnum.CONNECT_CHANNEL);
      }

      // Update the query cache with the new integration data
      integrationCache.updated(queryClient, resp);

      // Invalidate voice providers cache so BYO voices refresh after key change
      const voiceTypes = [
        IntegrationType.TWILIO_VOICE,
        IntegrationType.TELNYX_VOICE,
        IntegrationType.WEB_VOICE,
      ];
      if (voiceTypes.includes(resp?.type)) {
        queryClient.invalidateQueries({ queryKey: [VOICE_PROVIDERS_ENDPOINT] });
      }

      toast.success(t('integrations.updated'), {
        description: variables?.mutationOptions?.successDescription,
      });
    },
    onError: (error, variables) => {
      if (variables?.mutationOptions?.hideErrors) {
        return;
      }
      toast.error(error.message);
    },
    onSettled: async (resp) => {
      try {
        verifyIntegrationAndUpdateQuery(resp);
      } catch (error) {
        console.warn('Verification failed, but update was successful:', error);
      }
    },
  });

  const clearWhatsAppTestingRules = useCallback(
    (queryClient: QueryClient, integration_id: string) => {
      const deletedIntegration = queryClient.getQueryData<WhatsAppIntegration>([
        endpoints.integration(deskId, integration_id),
      ]);
      if (
        deletedIntegration.type === 'whatsapp' &&
        deletedIntegration.config.test_numbers &&
        deletedIntegration.config.test_numbers.length > 0
      ) {
        deletedIntegration.config.test_numbers.forEach((tn) => {
          if (tn.rule_id) {
            deleteRule(tn.rule_id);
          }
        });
      }
    },
    [deleteRule, deskId]
  );

  const { mutate: createIntegration, status: createStatus } = useMutation<
    T,
    Error,
    NewIntegration<T>
  >({
    mutationFn: API.createIntegration,
    onSuccess: (resp) => {
      integrationCache.created(queryClient, resp);
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteIntegration,
    mutateAsync: deleteIntegrationAsync,
    status: deleteStatus,
  } = useMutation<
    Pick<Integration, 'desk_id' | 'integration_id'>,
    Error,
    Pick<Integration, 'desk_id' | 'integration_id'>
  >({
    mutationFn: API.deleteIntegration,
    onSuccess: (resp) => {
      clearWhatsAppTestingRules(queryClient, resp.integration_id);
      dispatch(popModal());
      navigate(`/${slug}/environments/${deskId}/integrations`, { replace: true });

      integrationCache.removed(queryClient, { parentId: deskId, id: resp.integration_id });
      toast.success(t('integrations.deleted'));
    },
    meta: { errorToast: true },
  });

  const getIntegration = useCallback(
    async (desk_id: string, integration_id: string): Promise<T> => {
      const cachedIntegration = queryClient.getQueryData<T>([
        endpoints.integration(desk_id, integration_id),
      ]);

      if (cachedIntegration) {
        return cachedIntegration;
      }
      const integration = await API.getIntegration<T>(desk_id, integration_id);

      queryClient.setQueryData<T>(
        [endpoints.integration(integration.desk_id, integration.integration_id)],
        (prev) => ({ ...prev, ...integration })
      );

      return integration;
    },
    [queryClient]
  );
  const isVoiceIntegration = [
    IntegrationType.TWILIO_VOICE,
    IntegrationType.TELNYX_VOICE,
    IntegrationType.WEB_VOICE,
  ].includes(integration?.type);

  return {
    integrations: integrations?.integrations,
    listStatus,
    integration,
    isVoiceIntegration,
    getStatus,
    getError,
    createStatus,
    deleteStatus,
    updateStatus,
    updateError,
    verifyStatus,
    generateApiKeyStatus,

    getIntegration,
    createIntegration,
    deleteIntegration,
    deleteIntegrationAsync,
    updateIntegration,
    verifyIntegration,
    generateApiKey,
  };
};
